import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  BookOpenIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { DocumentArrowUpIcon } from '@heroicons/react/24/solid';

function FileUpload({
  selectedFile,
  handleFileSelect,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  handleUpload,
  isUploading,
  error,
  uploadResult,
  fileType,
  setFileType
}) {
  const [isDragging, setIsDragging] = useState(false);

  const onDragOver = (e) => {
    handleDragOver(e);
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    handleDragLeave(e);
    setIsDragging(false);
  };

  const onDrop = (e) => {
    handleDrop(e);
    setIsDragging(false);
  };

  const clearFile = () => {
    const input = document.getElementById('file-input');
    if (input) input.value = '';
    handleFileSelect({ target: { files: [] } });
  };

  const fileTypeOptions = [
    { value: 'requirements', label: 'Requirements', icon: DocumentTextIcon, color: 'primary' },
    { value: 'rules', label: 'Rules (Optional)', icon: BookOpenIcon, color: 'secondary' }
  ];

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    else return Math.round(bytes / 1048576) + ' MB';
  };

  // Get accepted file formats based on file type
  const getAcceptedFormats = () => {
    if (fileType === 'requirements') {
      return '.xlsx,.xls,.docx,.doc,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';
    }
    return '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';
  };

  const getSupportedFormatsText = () => {
    if (fileType === 'requirements') {
      return 'Supported formats: XLSX, XLS, DOCX, DOC (Max size: 10MB)';
    }
    return 'Supported formats: XLSX, XLS (Max size: 10MB)';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="card p-6"
    >
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
          Upload File
        </h2>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Upload your requirements or rules file for processing
        </p>
      </div>

      {/* File Type Selection */}
      <div className="mb-6">
        <label className="input-label mb-3">Select File Type</label>
        <div className="grid grid-cols-2 gap-3">
          {fileTypeOptions.map((option) => (
            <motion.button
              key={option.value}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setFileType(option.value)}
              className={`relative p-4 rounded-lg border-2 transition-all ${
                fileType === option.value
                  ? option.color === 'primary'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-secondary-500 bg-secondary-50 dark:bg-secondary-900/20'
                  : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  fileType === option.value
                    ? option.color === 'primary'
                      ? 'bg-primary-100 dark:bg-primary-800/30'
                      : 'bg-secondary-100 dark:bg-secondary-800/30'
                    : 'bg-neutral-100 dark:bg-neutral-800'
                }`}>
                  <option.icon className={`w-5 h-5 ${
                    fileType === option.value
                      ? option.color === 'primary'
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-secondary-600 dark:text-secondary-400'
                      : 'text-neutral-600 dark:text-neutral-400'
                  }`} />
                </div>
                <div className="text-left">
                  <div className="font-medium text-neutral-900 dark:text-white">
                    {option.label}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    {option.value === 'requirements' ? 'Requirement files' : 'Stellantis rule files'}
                  </div>
                </div>
              </div>
              {fileType === option.value && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-2 right-2"
                >
                  <CheckCircleIcon className={`w-5 h-5 ${
                    option.color === 'primary'
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-secondary-600 dark:text-secondary-400'
                  }`} />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Drop Zone */}
      <motion.div
        animate={{
          scale: isDragging ? 1.02 : 1,
          borderColor: isDragging ? 'rgb(102, 126, 234)' : undefined
        }}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
            : 'border-neutral-300 dark:border-neutral-700 hover:border-neutral-400 dark:hover:border-neutral-600'
        }`}
        onDragOver={onDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          id="file-input"
          type="file"
          accept={getAcceptedFormats()}
          onChange={handleFileSelect}
          className="hidden"
        />

        {!selectedFile ? (
          <>
            <motion.div
              animate={{ y: isDragging ? -5 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <CloudArrowUpIcon className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-primary-500' : 'text-neutral-400 dark:text-neutral-500'
              }`} />
            </motion.div>
            <p className="text-base font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              {isDragging ? 'Drop your file here' : 'Drag and drop your file here'}
            </p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
              or
            </p>
            <label
              htmlFor="file-input"
              className="btn-primary inline-flex items-center gap-2 cursor-pointer"
            >
              <ArrowUpTrayIcon className="w-5 h-5" />
              Browse Files
            </label>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-4">
              {getSupportedFormatsText()}
            </p>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <DocumentIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-neutral-900 dark:text-white">
                  {selectedFile.name}
                </p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {formatFileSize(selectedFile.size)} • {fileType === 'requirements' ? 'Requirements' : 'Rules'}
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={clearFile}
              className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
            </motion.button>
          </motion.div>
        )}
      </motion.div>

      {/* Error Message */}
      <AnimatePresence>
        {error && !uploadResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-4 p-4 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800"
          >
            <div className="flex items-start gap-3">
              <XCircleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger-700 dark:text-danger-400">
                {error}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Button */}
      <motion.div className="mt-6">
        <button
          onClick={handleUpload}
          disabled={isUploading || !selectedFile}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {isUploading ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <DocumentArrowUpIcon className="w-5 h-5" />
              <span>Upload File</span>
            </>
          )}
        </button>
      </motion.div>

      {/* Upload Result */}
      <AnimatePresence>
        {uploadResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className={`mt-4 p-4 rounded-lg border ${
              uploadResult.success
                ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                : 'bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-800'
            }`}
          >
            <div className="flex items-start gap-3">
              {uploadResult.success ? (
                <CheckCircleIcon className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <h4 className={`font-medium mb-1 ${
                  uploadResult.success
                    ? 'text-success-800 dark:text-success-300'
                    : 'text-danger-800 dark:text-danger-300'
                }`}>
                  {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                </h4>
                <p className={`text-sm ${
                  uploadResult.success
                    ? 'text-success-700 dark:text-success-400'
                    : 'text-danger-700 dark:text-danger-400'
                }`}>
                  {uploadResult.message}
                </p>
                {uploadResult.success && uploadResult.data && (
                  <div className="mt-2 pt-2 border-t border-success-200 dark:border-success-800">
                    <dl className="text-sm">
                      <div className="flex gap-2">
                        <dt className="text-success-600 dark:text-success-400">File:</dt>
                        <dd className="text-success-700 dark:text-success-300 font-medium">
                          {uploadResult.data.name}
                        </dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-success-600 dark:text-success-400">Type:</dt>
                        <dd className="text-success-700 dark:text-success-300 font-medium">
                          {fileType === 'requirements' ? 'Requirements' : 'Rules'}
                        </dd>
                      </div>
                      {uploadResult.data.file_standard && (
                        <div className="flex gap-2">
                          <dt className="text-success-600 dark:text-success-400">Format:</dt>
                          <dd className="text-success-700 dark:text-success-300 font-medium">
                            {uploadResult.data.file_standard}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default FileUpload;